import { useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { validateUpload } from '@/lib/uploadPolicy';

export interface UploadProgress {
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

export interface UploadedFile {
  storagePath: string;
  fileName: string;
  contentType: string;
}

export function useFileUpload() {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);

  const uploadFile = useCallback(async (file: File): Promise<UploadedFile> => {
    setUploadProgress({ progress: 0, status: 'uploading' });

    try {
      // Reject disallowed types before touching the network; the backend
      // enforces the same allowlist.
      const validation = validateUpload(file);
      if (!validation.ok) throw new Error(validation.error);
      const { contentType } = validation;

      // Step 1: Get presigned URL from backend
      const presignedRes = await apiFetch('/uploads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: file.name,
          contentType,
        }),
      });

      if (!presignedRes.ok) {
        const data = (await presignedRes.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error || 'Failed to get upload URL');
      }

      const { uploadUrl, storagePath } = (await presignedRes.json()) as {
        uploadUrl: string;
        storagePath: string;
        expiresAt: string;
      };

      // Step 2: Upload file to GCS using presigned URL
      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          // Must match the contentType the URL was signed with.
          'Content-Type': contentType,
        },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error('Failed to upload file to storage');
      }

      // Update progress to success
      setUploadProgress({ progress: 100, status: 'success' });

      // Clear progress after 2 seconds
      setTimeout(() => {
        setUploadProgress(null);
      }, 2000);

      return {
        storagePath,
        fileName: file.name,
        contentType,
      };
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Upload failed';
      setUploadProgress({
        progress: 0,
        status: 'error',
        error,
      });

      // Clear progress after 5 seconds
      setTimeout(() => {
        setUploadProgress(null);
      }, 5000);

      throw new Error(error);
    }
  }, []);

  const clearProgress = useCallback(() => {
    setUploadProgress(null);
  }, []);

  return {
    uploadFile,
    uploadProgress,
    clearProgress,
  };
}
